import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Bitcoin, Droplets, Link2, Users, Radio } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LivePayoutFeed = () => {
  const [payouts, setPayouts] = useState([]);

  useEffect(() => {
    const fetchPayouts = async () => {
      try {
        const res = await axios.get(`${API}/public/recent-payouts?limit=15`);
        setPayouts(res.data);
      } catch (err) {
        // silent fail
      }
    };

    fetchPayouts();
    const interval = setInterval(fetchPayouts, 8000);
    return () => clearInterval(interval);
  }, []);

  const getIcon = (type) => {
    switch (type) {
      case 'click':
        return <Link2 className="w-4 h-4 text-cyan-400" />;
      case 'faucet':
        return <Droplets className="w-4 h-4 text-emerald-400" />;
      case 'referral':
        return <Users className="w-4 h-4 text-purple-400" />;
      default:
        return <Bitcoin className="w-4 h-4 text-[#F7931A]" />;
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'click':
        return 'Click Payout';
      case 'faucet':
        return 'Faucet Claim';
      case 'referral':
        return 'Referral Bonus';
      default:
        return 'Payout';
    }
  };

  const timeAgo = (dateStr) => {
    const secs = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
    if (secs < 60) return `${secs}s ago`;
    if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
    if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
    return `${Math.floor(secs / 86400)}d ago`;
  };

  if (payouts.length === 0) {
    return null;
  }

  // Duplicate the array for seamless infinite scroll
  const scrollPayouts = [...payouts, ...payouts];

  return (
    <div className="relative w-full overflow-hidden py-4" data-testid="live-payout-feed">
      {/* Header */}
      <div className="flex items-center justify-center gap-2 mb-4">
        <div className="relative flex items-center justify-center">
          <div className="w-2 h-2 rounded-full bg-emerald-400 animate-ping absolute" />
          <div className="w-2 h-2 rounded-full bg-emerald-400" />
        </div>
        <Radio className="w-4 h-4 text-emerald-400" />
        <span className="text-xs font-mono uppercase tracking-widest text-emerald-400">
          Live Payouts
        </span>
      </div>

      {/* Gradient masks */}
      <div className="absolute left-0 top-12 bottom-0 w-24 bg-gradient-to-r from-[#07080D] to-transparent z-10 pointer-events-none" />
      <div className="absolute right-0 top-12 bottom-0 w-24 bg-gradient-to-l from-[#07080D] to-transparent z-10 pointer-events-none" />

      {/* Ticker */}
      <div className="flex gap-3 animate-scroll">
        {scrollPayouts.map((payout, idx) => (
          <div
            key={`${payout.username}-${payout.created_at}-${payout.type}-${idx < payouts.length ? 'a' : 'b'}`}
            className="flex-shrink-0 flex items-center gap-3 px-4 py-2 rounded-full bg-[#131622] border border-white/10"
          >
            {getIcon(payout.type)}
            <span className="text-xs text-slate-400 font-mono">{payout.username}</span>
            <span className="text-xs text-slate-500">·</span>
            <span className="text-xs text-slate-400">{getTypeLabel(payout.type)}</span>
            <span className="text-xs text-slate-500">·</span>
            <span className="font-mono text-sm font-bold text-[#F7931A]">
              +{payout.amount_satoshis} sats
            </span>
            <span className="text-xs text-slate-500">{timeAgo(payout.created_at)}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default LivePayoutFeed;
